"use client";

/**
 * Client shell around the (server-rendered) marketing landing page that owns
 * the "edit this page" takeover. Inactive, it renders the marketing tree
 * byte-identically; on activation it swaps in the real editor (DemoEditorApp,
 * dynamically imported so TipTap/KaTeX never touch the landing bundle) behind
 * a brief scrim. Marketing DOM and the demo render in different trees on
 * purpose — the `.landing` CSS namespace unmounts with the marketing page and
 * can't bleed into editor chrome.
 *
 * The visitor's edits survive exiting/re-entering the demo within the visit
 * (docRef), and "Restore page" inside the demo resets to the pristine doc.
 */

import dynamic from "next/dynamic";
import { createContext, useCallback, useEffect, useRef, useState } from "react";

const DemoEditorApp = dynamic(() => import("../demo/DemoEditorApp"), { ssr: false });

/** Consumed by EditThisPageButton (hero + nav). Null until the shell mounts. */
export const DemoTriggerContext = createContext<{
  open: () => void;
  prefetch: () => void;
} | null>(null);

const MIN_SCRIM_MS = 600;

export default function LandingDemoShell({
  fontClass,
  children,
}: {
  fontClass: string;
  children: React.ReactNode;
}) {
  const [active, setActive] = useState(false);
  const [scrim, setScrim] = useState<"none" | "in" | "out">("none");
  // Latest doc across demo sessions this visit; null = pristine (the demo
  // chunk owns the default so the lesson JSON stays out of the landing bundle)
  const docRef = useRef<object | null>(null);
  const scrimStart = useRef(0);
  const readyRef = useRef(false);

  const prefetch = useCallback(() => {
    // Warm the async chunk on hover/focus so the open feels instant
    import("../demo/DemoEditorApp").catch(() => {});
  }, []);

  const open = useCallback(() => {
    // Desktop-only: the CTA is hidden below lg, but guard anyway (keyboard,
    // stale media queries after resize)
    if (!window.matchMedia("(min-width: 1024px)").matches) return;
    readyRef.current = false;
    scrimStart.current = performance.now();
    setScrim("in");
    setActive(true);
  }, []);

  // The editor reported ready → hold the scrim to its minimum, then lift it
  const onDemoReady = useCallback(() => {
    if (readyRef.current) return;
    readyRef.current = true;
    const elapsed = performance.now() - scrimStart.current;
    const wait = Math.max(0, MIN_SCRIM_MS - elapsed);
    window.setTimeout(() => {
      window.scrollTo(0, 0);
      setScrim("out");
      window.setTimeout(() => setScrim("none"), 300);
    }, wait);
  }, []);

  const exit = useCallback(() => {
    setActive(false);
    setScrim("none");
  }, []);

  // Belt and braces: leaving demo restores normal scrolling state
  useEffect(() => {
    if (!active) window.scrollTo(0, 0);
  }, [active]);

  return (
    <DemoTriggerContext.Provider value={{ open, prefetch }}>
      {active ? (
        <DemoEditorApp
          initialDoc={docRef.current}
          onDocChange={(doc) => {
            docRef.current = doc;
          }}
          onExit={exit}
          onReady={onDemoReady}
        />
      ) : (
        <div className={`landing ${fontClass}`}>{children}</div>
      )}
      {scrim !== "none" && (
        <div className={`ld-demo-scrim ${scrim === "out" ? "is-leaving" : ""}`} aria-hidden="true">
          <div className="ld-demo-scrim-inner">
            <span className="ld-demo-scrim-mark">
              Lesson<span>Fresh</span>
            </span>
            <p>Opening this page in the editor…</p>
            <span className="ld-demo-scrim-bar" />
          </div>
        </div>
      )}
    </DemoTriggerContext.Provider>
  );
}
